import {
  useFormContext,
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
} from "react-hook-form"

import { FormMoneyInput } from "@/components/form-elements/FormMoneyInput"
import { Card, CardContent } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { FieldInfo } from "@/components/ui/field-info"
import { FormControl, FormField, FormItem } from "@/components/ui/form"
import { EMPTY_AMOUNT } from "@/lib/fundready/schema"
import { cn } from "@/lib/utils"

export type FormToggleCardProps<T extends FieldValues = FieldValues> = {
  name: FieldPath<T>
  title: string
  description?: string
  amountName?: FieldPath<T>
  amountPlaceholder?: string
  // Optional encumbrance controls revealed while the card is on: an "under a
  // UCC" checkbox and a per-class "debt against it" input.
  uccName?: FieldPath<T>
  securedDebtName?: FieldPath<T>
  securedDebtLabel?: string
  securedDebtInfo?: string
}

export function FormToggleCard<T extends FieldValues = FieldValues>({
  name,
  title,
  description,
  amountName,
  amountPlaceholder,
  uccName,
  securedDebtName,
  securedDebtLabel,
  securedDebtInfo,
}: FormToggleCardProps<T>) {
  const form = useFormContext<T>()

  const setAmount = (field: FieldPath<T> | undefined, value: number) => {
    if (field) form.setValue(field, value as FieldPathValue<T, typeof field>)
  }

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        function toggle(next: boolean) {
          field.onChange(next)
          // Blank while on so amounts show their placeholder, 0 while off (no
          // asset held, still submit-ready). Clearing the UCC flag on the way
          // off keeps a hidden class from staying pooled.
          setAmount(amountName, next ? EMPTY_AMOUNT : 0)
          setAmount(securedDebtName, next ? EMPTY_AMOUNT : 0)
          if (uccName)
            form.setValue(uccName, false as FieldPathValue<T, typeof uccName>)
        }

        function toggleUcc(next: boolean) {
          if (uccName)
            form.setValue(uccName, next as FieldPathValue<T, typeof uccName>)
          // Under the UCC the per-class debt input hides, so zero it (the
          // shared balance covers this class); otherwise reveal it blank.
          setAmount(securedDebtName, next ? 0 : EMPTY_AMOUNT)
        }

        return (
          <FormItem className="space-y-0">
            <Card
              className={cn(
                "gap-3 rounded-lg border py-3.5 shadow-none transition-colors",
                field.value ? "border-primary bg-accent/40" : "border-input",
              )}
            >
              <CardContent className="flex flex-col gap-3 px-3.5">
                <div className="flex flex-wrap items-center gap-3">
                  <FormControl>
                    <Checkbox
                      id={name}
                      checked={field.value}
                      onCheckedChange={(checked) => toggle(!!checked)}
                    />
                  </FormControl>
                  <label
                    htmlFor={name}
                    className="min-w-0 flex-1 cursor-pointer"
                  >
                    <span className="block text-sm font-medium">{title}</span>
                    {description && (
                      <span className="text-muted-foreground block text-xs">
                        {description}
                      </span>
                    )}
                  </label>
                  {field.value && amountName && (
                    <div className="w-full sm:w-40">
                      <FormMoneyInput<T>
                        name={amountName}
                        placeholder={amountPlaceholder ?? ""}
                      />
                    </div>
                  )}
                </div>

                {field.value && uccName && (
                  <FormField
                    control={form.control}
                    name={uccName}
                    render={({ field: ucc }) => (
                      <div className="border-primary/20 flex flex-col gap-3 border-t pt-3">
                        <label className="flex items-center gap-2.5 text-sm">
                          <Checkbox
                            checked={ucc.value}
                            onCheckedChange={(checked) => toggleUcc(!!checked)}
                          />
                          <span>Pledged under a UCC (blanket lien)</span>
                          <FieldInfo text="A lender can file a legal claim against your business assets (e.g., accounts receivable, inventory, equipment) if they're backing a loan. Some liens cover just one asset; others - a 'blanket lien' - cover all of them." />
                        </label>
                        {!ucc.value && securedDebtName && (
                          <FormMoneyInput<T>
                            name={securedDebtName}
                            label={securedDebtLabel}
                            info={securedDebtInfo}
                            placeholder="$0"
                          />
                        )}
                      </div>
                    )}
                  />
                )}
              </CardContent>
            </Card>
          </FormItem>
        )
      }}
    />
  )
}
